import {
	Ref,
	computed,
	ref,
	watchEffect,
} from '@vue/composition-api';

export interface PaginationConfig {
	startIdx: number;
	endIdx: number;
}

interface Pagination<T> {
	/**
	 * The sub-array of items that are currently visible
	 */
	visible: Ref<T[]>;
	/**
	 * True if there are more non-visible items at the start of the items array
	 */
	canPageForward: Ref<boolean>;
	/**
	 * True if there are more non-visible items at the end of the items array
	 */
	canPageBackward: Ref<boolean>;
	/**
	 * Add more items to the end of the visible list. ie: increase the value of the
	 * endIdx
	 */
	pageForward(n: number): void;
	/**
	 * Add more items to the start of the visible list. ie: decrease the value of the
	 * startIdx
	 */
	pageBackward(n: number): void;
	/**
	 * Reset the start and end index, to reset the currently visible items. This is
	 * necessary if transitioning from an empty array to an array with some elements.
	 */
	setPage(config: PaginationConfig): void;
	startIdx: Ref<number>;
	endIdx: Ref<number>;
}

/**
 * A function to determine if any two elements from the items array are
 * considered equal or not
 */
type Equals<T> = (a: T, b: T) => boolean;

/**
 * This implements pagination without any knowledge of how many pages might be available.
 */
export function usePagination<T>(
	items: Ref<T[]>,
	config: PaginationConfig,
	id: Equals<T>,
): Pagination<T> {
	const firstItem = ref(null) as Ref<T | null>;
	const lastItem = ref(null) as Ref<T | null>;
	let hasInitialized = false;
	const stop = watchEffect(() => {
		if (hasInitialized) {
			stop();
			return;
		}
		if (items.value.length !== 0) {
			firstItem.value = getFirstItem(items.value, config.startIdx);
			lastItem.value = getLastItem(items.value, config.endIdx);
			if (hasInitialized && stop) {
				stop();
			}
			hasInitialized = true;
		}
	});

	const startIdx = computed(() => {
		const v = firstItem.value;
		if (!v) {
			return 0;
		}
		return items.value.findIndex((item) => id(item, v));
	});
	const endIdx = computed(() => {
		const v = lastItem.value;
		if (!v) {
			return 0;
		}
		return items.value.findIndex((item) => id(item, v));
	});

	const visible = computed(() => items.value.slice(startIdx.value, endIdx.value + 1));

	const canPageForward = computed(() => endIdx.value < items.value.length - 1);
	const canPageBackward = computed(() => startIdx.value > 0);

	const pageForward = (n: number) => {
		const nextIdx = Math.min(endIdx.value + n, items.value.length - 1);
		lastItem.value = items.value[nextIdx];
	};

	const pageBackward = (n: number) => {
		const nextIdx = Math.max(startIdx.value - n, 0);
		firstItem.value = items.value[nextIdx];
	};
	const setPage = (pageConfig: PaginationConfig) => {
		firstItem.value = getFirstItem(items.value, pageConfig.startIdx);
		lastItem.value = getLastItem(items.value, pageConfig.endIdx);
	};

	return {
		visible,
		canPageForward,
		canPageBackward,
		pageForward,
		pageBackward,
		setPage,
		startIdx,
		endIdx,
	};
}

function getFirstItem<T>(items: T[], idx: number): T {
	const boundedIdx = Math.max(Math.min(idx, items.length), 0);
	return items[boundedIdx];
}

function getLastItem<T>(items: T[], idx: number): T {
	const boundedIdx = Math.min(Math.max(idx, 0), items.length - 1);
	return items[boundedIdx];
}

// Page numbers are 1-indexed
const MIN_PAGE = 1;

export interface UsePagination {
	numPages: Ref<number>;
	/**
	 * The current page selected (one-indexed)
	 */
	currentPage: Ref<number>;
	/**
	 * @param page A one-indexed page to go to
	 */
	goToPage(page: number): void;
	goToNextPage(): void;
	hasNextPage: Ref<boolean>;
	goToPreviousPage(): void;
	hasPrevPage: Ref<boolean>;
}

/**
 * Implements pagination for a given set of items. Calculates # of pages, etc.
 * based on the current state of the `items`, and `pageLength` parametes
 */
export function usePaginationFromList<T>(
	items: Ref<T[]>,
	pageLength: number,
	initialPage: number,
): UsePagination {
	const numPages = computed(() => Math.ceil(items.value.length / pageLength));
	const currentPageInner = ref(clampNumber(initialPage, 0, numPages.value));
	const currentPage = computed(() => clampNumber(currentPageInner.value, MIN_PAGE, numPages.value));
	const goToPage = (newPage: number) => {
		currentPageInner.value = clampNumber(newPage, MIN_PAGE, numPages.value);
	};
	const goToNextPage = () => {
		goToPage(currentPage.value + 1);
	};
	const goToPreviousPage = () => {
		goToPage(currentPage.value - 1);
	};
	const hasNextPage = computed(() => currentPage.value < numPages.value);
	const hasPrevPage = computed(() => currentPage.value > 1);
	return {
		numPages,
		currentPage,
		goToPage,
		goToNextPage,
		hasNextPage,
		goToPreviousPage,
		hasPrevPage,
	};
}

/**
 * Implements pagination for a set of items where the # and pageLength are known,
 * but the items themselves may not be currently available
 */
export function usePaginationFromCount(
	numItems: Ref<number>,
	pageLength: number,
	initialPage: number,
): UsePagination {
	const numPagesToRender = computed(() => Math.ceil(numItems.value / pageLength));
	const currentPageInner = ref(clampNumber(initialPage, 0, numPagesToRender.value));
	const currentRenderPage = computed(() => clampNumber(currentPageInner.value, MIN_PAGE, numPagesToRender.value));
	const goToPage = (newPage: number) => {
		currentPageInner.value = clampNumber(newPage, MIN_PAGE, numPagesToRender.value);
	};
	const goToNextPage = () => {
		const next = currentRenderPage.value + 1;
		goToPage(next);
	};
	const goToPreviousPage = () => {
		const next = currentRenderPage.value - 1;
		goToPage(next);
	};
	const hasNextPage = computed(() => currentRenderPage.value < numPagesToRender.value);
	const hasPrevPage = computed(() => currentRenderPage.value > 1);
	return {
		numPages: numPagesToRender,
		currentPage: currentRenderPage,
		goToPage,
		goToNextPage,
		hasNextPage,
		goToPreviousPage,
		hasPrevPage,
	};
}

function clampNumber(request: number, min: number, max: number) {
	return Math.max(min, Math.min(request, max));
}
